<script setup lang="ts">
import type { FormInputProps } from '~kui/types/input';

import dayjs from 'dayjs';

interface Props extends FormInputProps {
  id?: string;
  dateFormat: string;
  label: string;
  required?: boolean;
  hideLabel?: boolean;
  hideChildLabel?: boolean;
  dataTestId?: string;
  minAge?: number;
  version?: 'v1' | 'v2';
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  id: undefined,
  inputTypeState: undefined,
  dataTestId: 'date-picker',
  required: false,
  hideLabel: false,
  minAge: 0,
  version: 'v1',
  disabled: false,
});
const emit = defineEmits<{
  blur: [];
}>();

const modelValue = defineModel<string | null>({ required: false });

const handleFocusOut = () => {
  emit('blur');
};

const month = ref<string | null>(null);
const year = ref<string | null>(null);

onMounted(() => {
  if (modelValue.value) {
    const parsed = dayjs(modelValue.value);
    if (parsed.isValid()) {
      month.value = String(parsed.month() + 1);
      year.value = String(parsed.year());
    }
  }
});

const realValue = computed(() => {
  if (!month.value || !year.value) {
    return null;
  }
  return dayjs(new Date(Number(year.value), Number(month.value) - 1, 1)).format(props.dateFormat);
});

watch(realValue, () => {
  modelValue.value = realValue.value;
});

const months = Array.from({ length: 12 }, (_, i) => ({
  value: (i + 1).toString(),
  label: dayjs().month(i).format('MMMM'),
}));

const currentYear = dayjs().year();
const startYear = currentYear - props.minAge;
const totalYears = 66 - props.minAge;
const years = Array.from({ length: totalYears }, (_, i) => {
  const year = (startYear - i).toString();
  return { value: year, label: year };
});

const elementId = useId();
</script>

<template>
  <div>
    <KUICoreInputLabel
      v-if="!hideLabel && label"
      :label="label"
      :for="id ? id : elementId"
      :required="required"
    />
    <div class="grid grid-cols-2 gap-2" @focusout="handleFocusOut">
      <KUICoreInputCombobox
        :id="`${id ? id : elementId}-month`"
        v-model="month"
        label="Month"
        :options="months"
        placeholder="Month"
        :data-test-id="`${dataTestId}-month-select`"
        :version="version"
        :input-type-state="inputTypeState"
        :hide-label="hideChildLabel"
        :disabled="disabled"
      />
      <KUICoreInputCombobox
        :id="`${id ? id : elementId}-year`"
        v-model="year"
        label="Year"
        :options="years"
        placeholder="Year"
        :data-test-id="`${dataTestId}-year-select`"
        :version="version"
        :input-type-state="inputTypeState"
        :hide-label="hideChildLabel"
        :disabled="disabled"
      />
    </div>
  </div>
</template>
